import * as React from 'react'

import * as plume from '@ulule/owl-kit-components/next'

import * as service from '@owl-nest/services'
import { useTracking, SITE, EVENT_TYPE, CATEGORY } from '@owl-nest/shadow'
import { AuthenticatedUser } from '@owl-nest/api-client/latest'

import { ExistingUser } from './ExistingUser'
import { UnknownUser } from './UnknownUser'
import { NewUser } from './NewUser'
import { KnownUser } from './KnownUser'
import { Identity } from './Identity'

import * as S from './styles'

type ConnectProps<EXTRA_FIELDS extends string = ''> = {
  allowGuest?: boolean
  compactMode?: boolean
  extraFields?: EXTRA_FIELDS[]
  onGuestLoggedIn?: (user: AuthenticatedUser<EXTRA_FIELDS>) => void
  onLoggedIn?: (user: AuthenticatedUser<EXTRA_FIELDS>) => void
  onSignedUp?: (user: AuthenticatedUser) => void
  translations?: Translations
}

type Translations = {
  unknownGuestUser: {
    cta: string
  }
  existingUser: {
    cta: string
  }
}

export function Connect<EXTRA_FIELDS extends string = ''>({
  allowGuest,
  compactMode = false,
  extraFields = [],
  onGuestLoggedIn,
  onLoggedIn,
  onSignedUp,
  translations,
}: ConnectProps<EXTRA_FIELDS>): React.ReactElement<ConnectProps<EXTRA_FIELDS>> | null {
  const tracking = useTracking()
  const auth = service.user.useAuth({
    onSignedUp: (user) => {
      tracking.track({
        event: EVENT_TYPE.GENERIC,
        siteCategory: SITE.CONNECT,
        eventCategory: CATEGORY.REGISTRATION,
        eventAction: 'Done',
        eventLabel: 'Ulule',
        ululeUserId: `${user.id}`,
      })
      onSignedUp && onSignedUp(user)
    },
    onGuestLoggedIn,
    onLoggedIn,
    extraFields,
  })

  return (
    <S.Wrapper compactMode={compactMode}>
      {auth.type === 'initial' ? (
        <plume.Spinner />
      ) : auth.type === 'unknown' ? (
        <UnknownUser auth={auth} allowGuest={allowGuest} translations={translations?.unknownGuestUser} />
      ) : auth.type === 'known' ? (
        <KnownUser auth={auth} />
      ) : auth.type === 'existing' ? (
        <ExistingUser auth={auth} translations={translations?.existingUser} />
      ) : auth.type === 'new' ? (
        <NewUser auth={auth} />
      ) : auth.type === 'loggedin' ? (
        <Identity />
      ) : (
        <></>
      )}
    </S.Wrapper>
  )
}
